import PropTypes from 'prop-types'
import React from 'react'
import classNames from 'classnames'

import { sendSelectClickSoundCommand, sendHoverSoundCommand } from 'react-wot-bootstrap'

import { KEY_CODES } from 'constants'

import styles from './VehicleFilterSelect.scss'

const propTypes = {
    caption: PropTypes.node,
    captionPosition: PropTypes.oneOf(['left', 'top']),
    items: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]).isRequired,
        caption: PropTypes.node.isRequired,
        icon: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.node,
        ]),
    })).isRequired,
    selectedItem: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]).isRequired,
    width: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    isDisabled: PropTypes.bool,
    isJustified: PropTypes.bool,
    onItemChange: PropTypes.func.isRequired,
    onToggled: PropTypes.func,
    tooltipId: PropTypes.string,
}

const defaultProps = {
    captionPosition: 'left',
    onToggled: () => {},
}

const MIN_WIDTH = 40
const MAX_WIDTH = 300

export class VehicleFilterSelect extends React.PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            isActive: false,
            selectWidth: null,
            selectedItem: props.selectedItem,
            dropdownPositionY: 'bottom',
            dropdownPositionX: 'left',
        }

        this.handleToggle = this.handleToggle.bind(this)
        this.handleBlur = this.handleBlur.bind(this)
        this.handleKeyDown = this.handleKeyDown.bind(this)
        this.handleItemChange = value => this.changeItem.bind(this, value)
        this.setPosition = this.setPosition.bind(this)
    }

    componentDidMount() {
        this.updateDropdownWidth()
        document.addEventListener('keydown', this.handleKeyDown)
    }

    componentWillReceiveProps(nextProps) {
        this.updateDropdownWidth()

        if (this.state.selectedItem !== nextProps.selectedItem) {
            this.setState({ selectedItem: nextProps.selectedItem })
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown)
    }

    hide() {
        const isActive = false
        this.setState({ isActive })
        this.props.onToggled(isActive)
    }

    handleToggle() {
        const isActive = !this.state.isActive
        this.setPosition()
        this.setState({ isActive })
        this.props.onToggled(isActive)
    }

    handleBlur() {
        this.hide()
        this.resetItem()
    }

    handleKeyDown({ keyCode }) {
        if (!this.state.isActive) {
            return
        }

        if (keyCode === KEY_CODES.ESCAPE) {
            this.handleBlur()
        } else if (keyCode === KEY_CODES.ENTER || keyCode === KEY_CODES.SPACE) {
            this.changeItem(this.state.selectedItem)
        } else if (keyCode === KEY_CODES.UP || keyCode === KEY_CODES.DOWN) {
            this.selectItem(keyCode)
        }
    }

    selectItem(keyCode) {
        const selectedItemIdx = this.props.items.findIndex(item => item.value === this.state.selectedItem)
        const nextSelectedItemIdx = keyCode === KEY_CODES.UP ?
            Math.max(0, selectedItemIdx - 1) :
            Math.min(this.props.items.length - 1, selectedItemIdx + 1)
        const nextSelectedItem = this.props.items[nextSelectedItemIdx].value

        if (this.state.selectedItem !== nextSelectedItem) {
            this.setState({ selectedItem: nextSelectedItem })
        }
    }

    resetItem() {
        if (this.state.selectedItem !== this.props.selectedItem) {
            this.setState({ selectedItem: this.props.selectedItem })
        }
    }

    changeItem(selectedItem) {
        if (selectedItem !== this.props.selectedItem) {
            this.props.onItemChange(selectedItem)
        } else if (this.state.selectedItem !== this.props.selectedItem) {
            this.resetItem()
        }

        this.hide()
        sendSelectClickSoundCommand()
    }

    updateDropdownWidth() {
        if (this._dropdown) {
            let width = Math.max(this._dropdown.offsetWidth, MIN_WIDTH)

            this.setState({ selectWidth: width })
        }
    }

    renderItems() {
        return this.props.items.map(item => {
            const icon = item.icon ? (
                <span className={styles.icon}>{item.icon}</span>
            ) : null

            const itemClassName = classNames(styles.item, {
                [styles.isSelected]: this.state.selectedItem === item.value,
            })

            return (
                <li
                    className={itemClassName}
                    key={`${item.value}`}
                    onClick={this.handleItemChange(item.value)}
                    onMouseEnter={sendHoverSoundCommand}
                >
                    <span className={styles.itemContainer}>
                        {icon}
                        {item.caption}
                    </span>
                </li>
            )
        })
    }

    renderCaption() {
        if (!this.props.caption) {
            return null
        }

        const classNameCaption = classNames(
            styles.caption,
            styles[`caption__${this.props.captionPosition}`]
        )

        return (
            <span className={classNameCaption}>{this.props.caption}</span>
        )
    }

    setPosition() {
        const { clientHeight, clientWidth } = document.documentElement
        const dropdownOffset = this._dropdown.getBoundingClientRect()

        const offsetBottom = this.state.dropdownPositionY === 'bottom' ?
            dropdownOffset.bottom :
            dropdownOffset.bottom + this._dropdown.offsetHeight + this._select.offsetHeight

        const offsetRight = this.state.dropdownPositionX === 'left' ?
            dropdownOffset.right :
            dropdownOffset.right + this._dropdown.offsetWidth + this._select.offsetWidth

        this.setState({
            dropdownPositionY: offsetBottom > clientHeight ? 'top' : 'bottom',
            dropdownPositionX: offsetRight > clientWidth ? 'right' : 'left',
        })
    }

    render() {
        const classNameBase = classNames(styles.base, {
            [styles.isActive]: this.state.isActive,
            [styles.isDisabled]: this.props.isDisabled,
            [styles.isJustified]: this.props.isJustified,
        })

        const classNameDropdown = classNames(styles.dropdown, {
            [styles[this.state.dropdownPositionY]]: this.state.dropdownPositionY,
            [styles[this.state.dropdownPositionX]]: this.state.dropdownPositionX,
        })

        const caption = this.renderCaption()

        const selectedItem = this.props.items.find(obj => obj.value === this.state.selectedItem) || {}

        let selectWidth = this.props.width
        if (!selectWidth) {
            selectWidth = this.state.selectWidth < MAX_WIDTH ? this.state.selectWidth : MAX_WIDTH
        }
        let dropdownWidth = selectWidth

        const dataAttrs = this.props.tooltipId ? {
            'data-tip': true,
            'data-for': this.props.tooltipId,
        } : {}

        return (
            <div
                className={classNameBase}
                tabIndex={1}
                onBlur={this.handleBlur}
            >
                {caption}
                <div
                    ref={(c) => this._select = c}
                    className={styles.selectContainer}
                    {...dataAttrs}
                >
                    <div
                        className={styles.decorator}
                        style={{ width: selectWidth }}
                    >
                        <div
                            className={styles.select}
                            onClick={this.props.isDisabled ? null : this.handleToggle}
                            onMouseDown={this.props.isDisabled ? null : sendSelectClickSoundCommand}
                            onMouseEnter={this.props.isDisabled ? null : sendHoverSoundCommand}
                        >
                            {
                                selectedItem.icon ? (
                                    <span className={styles.icon}>{selectedItem.icon}</span>
                                ) : selectedItem.caption
                            }
                        </div>
                    </div>
                    <menu
                        ref={(c) => this._dropdown = c}
                        className={classNameDropdown}
                        style={{ width: dropdownWidth }}
                    >
                        {this.renderItems()}
                    </menu>
                </div>
            </div>
        )
    }
}

VehicleFilterSelect.propTypes = propTypes
VehicleFilterSelect.defaultProps = defaultProps

export default VehicleFilterSelect



// WEBPACK FOOTER //
// ./components/VehicleFilterSelect/VehicleFilterSelect.jsx